import { TApiParameter } from "../api/endpoints/Parameter";
import { TApiSubset } from '../api/endpoints/Subset';
import { TLevelistOption } from "../api/endpoints/SubsetOption";
import { TApiVolumeBand } from "../api/endpoints/VolumeBand";
import { TParamLevelistValue } from "../components/RealtimeProductConfigurator/ParamLevelistSelector";
import { Area } from '../entities/Area';
import { Day } from '../entities/Day';
import { DayStepRange } from '../entities/DayStepRange';
import { LeveledParam } from '../entities/LeveledParam';
import { Resolution } from '../entities/Resolution';
import { StepRange } from '../entities/StepRange';
import { partitionByDiff } from "./Array";


const mapDayToString = (day: Day | number) => {
  switch (day) {
    case 0:
      return 'Monday';
    case 1:
      return 'Tuesday';
    case 2:
      return 'Wednesday';
    case 3:
      return 'Thursday';
    case 4:
      return 'Friday';
    case 5:
      return 'Saturday';
    case 6:
      return 'Sunday';
    default:
      return 'N/A'
  }
}

const convertToPaddedNumber = (n: string | number, maxLength: number = 2) => n.toString().padStart(maxLength, '0');

/**
 * Create a formatter which accepts an undefined param.
 */
const makeNull = <T, O1, O2>(formatter: (arg: T) => O1, def: O2) => (arg?: T) =>
  arg == null ? def : formatter(arg);

const orderNumbers = (arr: number[]) => arr.slice().sort((a, b) => a - b);

export const formatResolution = (res?: Resolution) => (res ? res.name : 'None');

export const formatRun = (run: Day | number): string => {
  return mapDayToString(run);
};

export const formatHindcastDay = (day: Day | number): string => {
  return mapDayToString(day);
}

export const formatRunRange = (value: DayStepRange): string =>
  `${formatRun(value.day)}/${formatStepRange(value.range)}`;

export const formatDayStepRanges = makeNull(
  (value: DayStepRange[]) => value.map(formatRunRange),
  'None'
);

export const formatDirection = (direction: number | string) => convertToPaddedNumber(direction);

export const formatFrequency = (frequency: number | string) => convertToPaddedNumber(frequency);

export const formatBaseTime = (time: number | string) => convertToPaddedNumber(time);

export const formatBaseTimes = makeNull(
  (value: number[]) => orderNumbers(value).map(formatBaseTime),
  ['None']
);

export const formatStepRange = (range: StepRange) => `${range.start}-${range.end}`;

export const formatLeveledParam = (param: LeveledParam | TParamLevelistValue) => `${param.param}/${param.level}`;

export const formatLevelist = (value: Array<string>) => value.map(Number).sort((a, b) => a - b).join(', ');

export const formatLeveledParams = makeNull(
  (value: LeveledParam[]) => value.map(formatLeveledParam),
  'None'
);

export const formatArea = makeNull((area: Area) => {
  const str: string[] = [];

  if (area.north != null) {
    str.push(`N: ${formatRounded(area.north, 3)}`);
  }

  if (area.west != null) {
    str.push(`W: ${formatRounded(area.west, 3)}`);
  }

  if (area.south != null) {
    str.push(`S: ${formatRounded(area.south, 3)}`);
  }

  if (area.east != null) {
    str.push(`E: ${formatRounded(area.east, 3)}`);
  }

  return str.join(' ');
}, 'Empty');

export const formatAreas = makeNull((areas: Area[]) => areas.map(a => formatArea(a)), 'Empty');

export const withDefault = <T>(value: T | undefined, func: (t: T) => string, def: string): string =>
  makeNull(func, def)(value);

export const formatInteger = (value: number): string =>
  value.toLocaleString('en-GB', {
    maximumFractionDigits: 0
  });

export const formatDecimal = (value: number): string =>
  value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

export const formatMegaBytes = (value: number): string => formatDecimal(value) + ' MB';

export const formatBinaryCapacityUnit = (n: number, precision: number = 2, unit?: string) => {
  const units = ["", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];

  let unitIndex = 0;

  while (n >= 1024 && (units[unitIndex] !== unit )) {
    n = n / 1024;
    unitIndex += 1;
  }

  return [n.toFixed(precision), units[unitIndex]].join(' ');
}

export const formatVolumeBand = (volumeBand: TApiVolumeBand) => {
  if (volumeBand.min_gb === 0) {
    return `<${volumeBand.max_gb} GiB`;
  }

  return `${volumeBand.min_gb} - ${volumeBand.max_gb} GiB`;
}

export const formatDailyVolumeLimit = (value: number) => {
  return formatBinaryCapacityUnit(value, 0, 'GiB');
}

export const formatCurrency = (value: number, precision: number = 2): string =>
  value.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: precision
  });

// JavaScript requires a song and a dance to get fixed DP without trailing zeroes
export const formatRounded = (value: number, dp: number): string => {
  return parseFloat(value.toFixed(dp)).toString();
};

export const formatSubsetLabel = (subset: TApiSubset) => {
  return `${subset.name}: ${subset.title}`;
};

export const formatParameterNameWithShortname = (param: TApiParameter) => {
  return `${param.name} (${param.shortname})`;
}

export const formatAggregatedSteps = (steps: Array<number>) => {
  const partitions = partitionByDiff(steps);

  return partitions.map(partition => {
    if (partition.items.length <= 3) {
      return partition.items.join(', ');
    } else {
      return `${partition.items[0]} to ${partition.items[partition.items.length - 1]} by ${partition.diff}`
    }
  }).join(', ');
};

export const formatBoolean = (value: boolean) => value ? 'Yes' : 'No';
